import { ArrowRight, Shield, LineChart, ListChecks } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSettings } from "@/lib/settings";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function Login() {
  const { settings } = useSettings();
  const brand = settings?.brand || { name: "Koinonia Church", letter: "K", tagline: "Operations · Tasks · Finance" };

  const handleGoogle = () => {
    window.location.href = `${BACKEND_URL}/api/auth/google/start`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      {/* Left: form */}
      <div className="flex flex-col justify-between p-8 md:p-14 lg:p-20 relative">
        <header className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-neutral-950 text-white grid place-items-center font-display font-semibold text-base">
            {brand.letter || "K"}
          </div>
          <div>
            <div className="font-display font-semibold text-lg leading-none">{brand.name}</div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mt-1">
              {brand.tagline}
            </div>
          </div>
        </header>

        <div className="max-w-md mt-12 rise" data-testid="login-hero">
          <div className="overline mb-4">A workspace for our team</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.02]">
            Steward the work.
            <br />
            Steward the finances.
          </h1>
          <p className="mt-5 text-neutral-600 text-base leading-relaxed">
            A simple, opinionated home for ministry tasks and church financials.
            Track work, log offerings &amp; expenses, and generate beautiful reports —
            in PDF or Excel — with one click.
          </p>

          <div className="mt-9">
            <Button
              data-testid="google-signin-button"
              onClick={handleGoogle}
              className="h-12 px-6 rounded-md bg-neutral-950 hover:bg-neutral-800 text-white font-medium text-[15px] flex items-center gap-3"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.7z" />
                <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.3 35 26.8 36 24 36c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.6l6.2 5.2c-.4.4 6.7-4.9 6.7-14.8 0-1.3-.1-2.4-.4-3.5z" />
              </svg>
              Continue with Google
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <p className="mt-3 text-xs text-neutral-500">
              Members must be invited by an admin first. If this is your first time,
              sign in with the email your admin invited.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-3 gap-6 text-sm">
            <Feature icon={ListChecks} label="Tasks &amp; subtasks" />
            <Feature icon={LineChart} label="Expense ledger" />
            <Feature icon={Shield} label="Role permissions" />
          </div>
        </div>

        <footer className="text-xs text-neutral-500 mt-12">
          © {new Date().getFullYear()} {brand.name} · Secured by Google OAuth
        </footer>
      </div>

      {/* Right: imagery */}
      <div className="hidden lg:block relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1567943183748-3a7542120c90?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-neutral-950/55 via-neutral-900/15 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <div className="overline text-white/70">Built for ministry teams</div>
          <p className="mt-3 font-display text-2xl max-w-md leading-snug">
            “Whatever you do, work at it with all your heart, as working for the Lord.” — Colossians 3:23
          </p>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, label }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="h-8 w-8 rounded-md border border-neutral-200 grid place-items-center">
        <Icon className="h-4 w-4 text-neutral-700" strokeWidth={1.5} />
      </div>
      <span className="text-[13px] text-neutral-700" dangerouslySetInnerHTML={{ __html: label }} />
    </div>
  );
}
